import React, {useEffect, useMemo, useRef, useState} from "react";
import IconAi from "../../../components/Icons/AiIcon";
import {useNavigate} from "react-router";
import Markdown from "react-markdown";
import {useTranslation} from "react-i18next";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "../../../components/Modal";
import CustomButton from "../../../components/CustomButton";
import TwoBars from "../../../components/Bars/TwoBars";
import { DueDateBadge, RangeOption, getFromDate } from "../../../components/Badges/DueDateBadge";
import SurveyBadge from "../../../components/Badges/SurveyBadge";
import ChatThreadInput from "../../AiThreads/components/ChatThreadInput";
import Api from "../../../services/Api";
import {getLanguage} from "../../../lang/helpers";
import ButtonMenu from "../../../components/TabMenu/ButtonMenu";
import {VisionStatementInterface, VisionStatementGoal, EditPart} from "../interfaces";
import { ChevronDown as IconDown } from "flowbite-react-icons/outline";
import PencilIcon from "../../../components/Icons/PencilIcon";
import iconAIThinking from "url:shared-assets/animations/sparkles_animated_infinite.svg"
import iconAIDone from "url:shared-assets/animations/sparkles_animated_ending.svg";
import {SurveyQuestionInterface} from "../../SurveyResult/interfaces";
import {UploadFile} from "../../AiThreads/interfaces";
import {renderFile} from "./renderUploadedFiles";
import DatePicker from 'react-datepicker';
import { useSetAtom } from 'jotai';
import { toastAtom } from '../../../jotai_state';
import InformationCircleIcon from "../../../components/Icons/InformationCircleIcon";

interface Props {
  visionStatement: VisionStatementInterface;
  editPart: EditPart;
  save: Function;
  viewGoal: VisionStatementGoal | null;
  clientName: string;
  close: Function;
}

interface ChatMsg {
  role: string;
  text: string;
  aiFiles?: UploadFile[]
}

enum StreamEnum {
  WAITING,
  THINKING,
  DONE
}

enum modelType {
  SUBGOAL = "goal", 
  ACTIVITY = "activity",
  BEHAVIOR = "behavior",
  LONGTERMGOAL = "vision_statement"
}

const formatDateToYMD = (d: Date): string => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const findMatchingRangeOption = (deadline: Date, options: RangeOption[]): RangeOption | null => {
  const deadlineStr = formatDateToYMD(deadline);
  for (const opt of options) {
    const optDateStr = formatDateToYMD(getFromDate(opt));
    if (deadlineStr === optDateStr) {
      return opt;
    }
  }
  return null;
};

const LongTermGoalPart = ((props: Props)=> {
  const api = Api.getInstance();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(); // i18n translation hook
  const lang = getLanguage(i18n.language);
  const chatWindowRef = useRef<HTMLDivElement>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [title, setTitle] = useState(props.editPart.title);
  const [ifWhen, setIfWhen] = useState(props.editPart.ifWhen ? props.editPart.ifWhen : "");
  const [ifThen, setIfThen] = useState(props.editPart.ifThen ? props.editPart.ifThen : "");
  const [howOften, setHowOften] = useState(props.editPart.howOften ?? 1);
  const [barrier, setBarrier] = useState(props.editPart.barrier ? props.editPart.barrier : "");
  const [description, setDescription] = useState(props.editPart.description);
  const [deadline, setDeadline] = useState<Date | null>(
    props.editPart.deadline ? new Date(props.editPart.deadline) : null
  );
  const [messages, setMessages] = useState<Array<ChatMsg>>([]);
  const [narrow, setNarrow] = useState(window.innerWidth < 1100);
  const [selectedView, setSelectedView] = useState("ai");
  const [prompt, setPrompt] = useState("");
  const [questions, setQuestions] = useState<Array<SurveyQuestionInterface>>([]);
  const [emmaThinking, setEmmaThinking] = useState(false);
  const [coachStyle, setCoachStyle] = useState<string>("assistant");
  const [streamStatus, setStreamStatus] = useState<StreamEnum>(StreamEnum.WAITING);
  const [suggestions, setSuggestions] = useState<Array<string>>([]);
  const [closeNoSave, setCloseNoSave] = useState(false);
  const setGlobalToast = useSetAtom(toastAtom);
  const [feedbackInfoModel, setFeedbackInfoModel] = useState(false);
  const [showfeedbackInfoModel, setShowFeedbackInfoModel] = useState(false);

  const makeOptions = (values: number[], unit: 'week' | 'month'): RangeOption[] =>
    values.map(value => ({ value, unit }));

  // OPTIONS_BY_TYPE: Determine which range to pass to the date badge.
  const OPTIONS_BY_TYPE: Record<modelType, RangeOption[]> = {
    [modelType.ACTIVITY]:     makeOptions([2, 4, 6, 8], 'week'),
    [modelType.BEHAVIOR]:     makeOptions([2, 4, 6, 8], 'week'),
    [modelType.SUBGOAL]:      makeOptions([1, 2], 'month'),
    [modelType.LONGTERMGOAL]: makeOptions([3, 6, 12], 'month'),
  };

  const currentOptions = OPTIONS_BY_TYPE[props.editPart.type as modelType] ?? null;

  // Derive active chip directly from the single source of truth (deadline)
  const selectedRangeOption = useMemo(() => {
    if (!deadline || !currentOptions) return null;
    return findMatchingRangeOption(deadline, currentOptions);
  }, [deadline, currentOptions]);

  const HOW_OFTEN_OPTIONS = [
    { value: 0, sv: "Varje dag",                 en: "Every day" },
    { value: 1, sv: "Flera gånger per vecka",    en: "Several times a week" },
    { value: 2, sv: "En gång per vecka",         en: "Once a week" },
  ];

  const onResize = () => {
    setNarrow(window.innerWidth < 1100);
  };

  const isValid = () => {
    if (props.editPart.type === modelType.BEHAVIOR) {
      if (ifWhen.length === 0 || ifThen.length === 0 || title.length === 0 || !deadline) return false;
    } else {
      if (description.length === 0 || title.length === 0 || !deadline) return false;
    }
    return true;
  };

  const getFirstCompletedSurveyRequest = () => {
    for (const sr of props.visionStatement.surveyRequests) {
      if (sr.completed) {
        return sr;
      }
    }
    return null;
  };

  useEffect(() => {
    window.addEventListener('resize', onResize);

    if (props.visionStatement.surveyRequests.length > 0) {
      const firstSurveyRequest = getFirstCompletedSurveyRequest();
      if (firstSurveyRequest)  {
        const _questions: Array<SurveyQuestionInterface> = [];
        for (const focusArea of firstSurveyRequest.surveyResult.focusAreas) {
          for (const question of focusArea.questions) {
            question.focusAreaName = focusArea.name;
            _questions.push(question);
          }
        }
        setQuestions(_questions);
        setShowFeedbackInfoModel(true);
      }
    }

    let editMessage = (props.editPart.deadline && props.editPart.deadline.length > 0);

    let startMessage = lang === "sv" ? "Hej" : "Hi";
    if (props.clientName.length > 0) {
      startMessage += " " + props.clientName;
    }

    if (editMessage) {
      if (props.editPart.type === modelType.LONGTERMGOAL) {
        if (lang === "sv") {
          startMessage += "! Vad vill du ändra eller förtydliga med din målbild? Berätta så hjälper jag dig vidare.";
          setSuggestions([
            "Hjälp mig göra denna målbild mer personlig",
            "Hjälp mig göra denna målbild mer motiverande",
            "Hjälp mig justera denna målbild så att den bättre matchar mina prioriteringar"
          ]);
        } else {
          startMessage += ", how can I help you edit your Goal?";
          setSuggestions([
            "Help me make this Goal more personal",
            "Help me make this Goal more motivating",
            "Help me adjust this Goal, so it matches my priorities better"
          ]);
        }
      } else if (props.editPart.type === modelType.SUBGOAL) {
        if (lang === "sv") {
          startMessage += "! Vad vill du ändra eller förtydliga med ditt delmål? Berätta så hjälper jag dig vidare.";
        } else {
          startMessage += ", how can I help you edit your Sub-goal?";
        }
      } else {
        if (lang === "sv") {
          startMessage += "! Vad vill du ändra eller förtydliga? Jag guidar dig gärna.";
        } else {
          startMessage += "! What would you like to change or clarify? I’m happy to guide you.";
        }
      }

    } else {
      if (props.editPart.type === modelType.LONGTERMGOAL) {
        if (lang === "sv") {

          if (props.editPart.title.length > 0) {
            startMessage += ", jag är här för att hjälpa dig skapa en tydlig och motiverande målbild.  \n  \n" +
                "Vill du att jag föreslår ett utifrån ditt utkast, eller vill du hellre reflektera över vad som är " +
                "viktigast för dig först?";
            setSuggestions([
              "Hjälp mig reflektera över vad som är viktigast",
              "Föreslå utifrån mitt utkast",
              "Visa mig hur en bra målbild kan se ut"
            ]);
          } else {
            startMessage += ", jag är här för att hjälpa dig skapa en tydlig och motiverande målbild.  \n  \n" +
                "Har du någon idé om hur du vill starta, eller vill du hellre reflektera över vad som är " +
                "viktigast för dig först?";
            setSuggestions([
              "Hjälp mig reflektera över vad som är viktigast",
              "Ge mig ett förslag",
              "Visa mig hur en bra målbild kan se ut"
            ]);
          }

        } else {

          if (props.editPart.title.length > 0) {
            startMessage += ", I'm here to help you create a clear and motivating Goal.  \n  \n" +
                "Would you like me to suggest one based on your draft, or would you rather reflect on what " +
                "matters most first?"
            setSuggestions([
              "Help me reflect on what matters most",
              "Suggest one based on my draft",
              "Show me what a good Goal looks like"
            ]);
          } else {
            startMessage += ", I'm here to help you create a clear and motivating Goal.  \n  \n" +
                "Do you have any ideas where to start, or would you rather reflect on what " +
                "matters most first?"
            setSuggestions([
              "Help me reflect on what matters most",
              "Give me a draft",
              "Show me what a good Goal looks like"
            ]);
          }
        }
      } else if (props.editPart.type === "action") {
        if (lang === "sv") {
          startMessage += ", låt oss ta fram en konkret uppgift du behöver göra för att nå delmålet.  \n " +
            "Har du något i åtanke redan eller vill du utforska tillsammans?";
          setSuggestions([
            "Hjälp mig utforska vad som passar min vardag",
            "Föreslå meningsfulla uppgifter utifrån mitt delmål",
            "Visa mig ett exempel på en konkret uppgift"
          ]);
        } else {
          startMessage += ", let's break this sub-goal into concrete tasks.  \n" +
            "Do you have something in mind, or want to explore together?"
          setSuggestions([
            "Help me explore what fits my everyday work",
            "Suggest meaningful tasks based on my sub-goal",
            "Show me an example of a concrete task"
          ]);
        }
      } else if (props.editPart.type === modelType.ACTIVITY) {
        if (lang === "sv") {
          startMessage += ", låt oss definiera en aktivitet som för dig närmare din målbild. " +
            "Har du redan något i åtanke, eller ska vi utforska tillsammans vad som passar utifrån var du är idag?";
          setSuggestions([
            "Hjälp mig utforska vad som passar",
            "Föreslå en kraftfull aktivitet utifrån min Målbild",
            "Visa mig ett exempel på en konkret aktivitet"
          ]);
        } else {
          startMessage += ", Let's define an activity that moves you closer to your Goal. " +
            "Do you already have something in mind, or shall we explore what makes sense based on where you are today?"
          setSuggestions([
            "Help me explore what fits",
            "Suggest one powerful activity based on my Goal",
            "Show me an example of a concrete activity"
          ]);
        }
      } else if (props.editPart.type === modelType.SUBGOAL) {
        if (lang === "sv") {
          startMessage += ", jag är här för att hjälpa dig definiera ett delmål som stödjer din målbild. " +
            "Vi kan använda SMART-metoden, eller utforska om ett utfallsbaserat, processbaserat eller " +
            "upplevelsebaserat mål passar bättre. Var vill du börja?"
          setSuggestions([
            "Hjälp mig välja rätt typ av mål",
            "Föreslå ett delmål utifrån min Målbild",
            "Förklara SMART vs andra måltyper"
          ]);
        } else {
          startMessage += ", I'm here to help you define a Sub-goal that supports your Goal. " +
            "We can use the SMART method, or explore whether an outcome-based, process-based, or experiential " +
            "goal fits better. Where would you like to start?"
          setSuggestions([
            "Help me choose the right type of goal",
            "Suggest a sub-goal based on my Goal",
            "Explain SMART vs other goal types"
          ]);
        }
      } else if (props.editPart.type === modelType.BEHAVIOR) {
        if (lang === "sv") {
          if (props.editPart.barrier) {
            startMessage += ", dags att skapa ett beteende som hjälper dig att nå ditt mål. Detta beteende kommer " +
              "baseras på ditt hinder &quot;" + props.editPart.barrier + "&quot;. Vi kommer att använda en enkel " +
              "&quot;När/Om → Då&quot;-struktur för att göra det tydligt och lätt att öva på. Vad vill du utgå ifrån?";
            setSuggestions([
              "Hjälp mig utforska detta hinder först",
              "Föreslå ett beteende",
              "Visa ett exempel på ett starkt beteende"
            ]);
          } else {
            startMessage += ", dags att skapa ett beteende som hjälper dig att nå ditt mål. Vi kommer att använda en " +
              "enkel &quot;När/Om → Då&quot;-struktur att göra det tydligt och lätt att öva på. Vad vill du utgå ifrån?";
            if (props.editPart.barriers) {
              const _barriers: Array<string> = [];
              for (const _barrier of props.editPart.barriers) {
                _barriers.push('Mitt hinder "' + _barrier.text + '"');
              }
              _barriers.push("Jag börjar hellre från början")
              setSuggestions(_barriers);
            }
          }

        } else {
          if (props.editPart.barrier) {
            startMessage += ", let's create a behaviour to help you reach this goal. This one will be based on your " +
              "barrier: &quot;" + props.editPart.barrier + "&quot;. We’ll use a simple &quot;If/When → Then&quot; " +
              "structure to make it clear and easy to practise. Where would you like to start?"
            setSuggestions([
              "Help me explore this barrier first",
              "Suggest one effective behaviour",
              "Show me an example of a strong behaviour"
            ]);
          } else {
            startMessage += ", let's create a behaviour to help you reach this goal. We’ll use a simple " +
              "&quot;If/When → Then&quot; structure to make it clear and easy to practise. What would you like to base it on?"
            if (props.editPart.barriers) {
              const _barriers: Array<string> = [];
              for (const _barrier of props.editPart.barriers) {
                _barriers.push('My barrier "' + _barrier.text + '"');
              }
              _barriers.push("I'd rather start from scratch")
              setSuggestions(_barriers);
            }
          }

        }
      }
    }

    if (startMessage.length > 0) {
      setEmmaThinking(true);
      const words = startMessage.split(' ');
      const wordLen = words.length;
      let _startMessage = "";
      let i = 0;
      let intervalId = setInterval(() => {
        if (i >= wordLen) {
          clearInterval(intervalId);
          setEmmaThinking(false);
        } else {
          _startMessage += words[i] + " ";
          setMessages([{
            role: "assistant",
            text: _startMessage
          }]);
        }
        i++;
      }, 80);
    }

    return () => {
      window.removeEventListener('resize', onResize);
    }
  }, []);

  const scrollDown = () => {
    if (chatWindowRef.current) {
      setTimeout(() => {
        const lastMsg = document.getElementById("last_msg");
        if (lastMsg && chatWindowRef.current) {
          chatWindowRef.current.scrollTo({
            top: lastMsg.offsetTop,
            left: 0,
            behavior: 'smooth',
          });
        }
      }, 200);
    }
  };

  const updateDeadline = (_deadline: Date | null) => {
    if (_deadline) {
      setDeadline(_deadline);
    } else {
      setDeadline(null);
    }
  };

  const renderChat = () => {
    const height = "calc(100dvh - " + (narrow ? 348 : 290) + "px)";
    const minHeight = narrow ? "340px" : "404px";
    const lastMsgNr = messages.length - 1;
    const lastMsgUserNr = messages.length - 2;
    return (
      <div ref={chatWindowRef} className="overflow-y-scroll z-no-scrollbar relative pb-4"
           style={{height: height, minHeight: minHeight}}>
        {
          messages.map((message: ChatMsg, i: number) => {
            let cls = "mt-2 text-gray-300 ";
            let lastId = i === lastMsgUserNr ? "last_msg" : ""
            if (message.role === "user") {
              cls +=
                " whitespace-pre-line px-4 py-2 bg-indigo-800 rounded-md align-right ml-auto max-w-[67%]";
            } else {
              cls += " markdown-content"
            }
            if (message.role === "user") {
              return (
                <div key={"chat_" + i} id={lastId}>
                  {
                    Array.isArray(message.aiFiles) && message.aiFiles.length > 0 &&
                    renderFile(message.aiFiles)
                  }
                  <div className={cls}>{message.text}</div>
                </div>
              )
            } else {
              return (
                <div key={"chat_" + i} className={cls} id={lastId}>
                  {
                    message.text === "SUGGESTION" ? (
                      <div className={(i === lastMsgNr) ? " z-last-msg-small" : ""}>
                        <div className="p-4 bg-indigo-800 border border-aquamarine-500 rounded-md flex items-center">
                          <IconAi className="h-5 w-5 text-success-500" aria-hidden="true" />
                          <div className="ml-2 z-title3 text-gray-100">
                            {
                              lang === "sv" ? "Skapade förslag. Se detaljer i manuella sektionen." :
                                "Suggestion created. View full details in the manual section."
                            }
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className={"flex items-start" + ((i > 0 && i === lastMsgNr) ? " z-last-msg-small" : "")}>
                        <img src={(i === lastMsgNr && streamStatus === StreamEnum.THINKING) ? iconAIThinking : iconAIDone}
                             alt="ai" className="mt-2 w-6"/>
                        <div className="ml-2">
                          <Markdown>
                            {message.text}
                          </Markdown>
                        </div>
                      </div>
                    )
                  }
                </div>
              )
            }

          })
        }
        {
          (messages.length === 1 && !emmaThinking && suggestions.length > 0) &&
            <>
            {
               suggestions.map((suggestion, index) => {
                 const fadeIn = index > 3 ? 3 : index;
                 return (
                  <CustomButton buttonType="neutralBase" className={"mt-2 z-anim-fade-in" + fadeIn} style={{width: "unset"}}
                                onClick={() => send(suggestion, null, [])}
                                key={"suggestion_" + index}>
                    <div className="ml-2 z-title3">{suggestion}</div>
                  </CustomButton>
                )
              })
            }
         </>
        }
      </div>
    )
  };

  const updateLastMessage = (txt: string) => {
    messages[messages.length - 1].text = txt;
    setMessages([...messages]);
  };

  async function handleStream(reader: ReadableStreamDefaultReader) {
    let done, value;
    let streamResponse = "";
    let finalResponse = "";
    const decoder = new TextDecoder();

    while (!done) {
      ({ value, done } = await reader.read());

      if (done) {
        if (finalResponse.length > 0) {
          try {
            const data = JSON.parse(finalResponse);
            if (data.aiResponse) {
              updateLastMessage(data.aiResponse);
              const suggestion = data.functionCall;
              if (suggestion) {
                setTitle(suggestion.title);
                if (suggestion.description) {
                  setDescription(suggestion.description);
                }
                if (suggestion.ifWhen) {
                  setIfWhen(suggestion.ifWhen);
                }
                if (suggestion.ifThen) {
                  setIfThen(suggestion.ifThen);
                }
                // Setting deadline automatically drives the derived selectedRangeOption
                setDeadline(new Date(suggestion.deadline));
                if (narrow) {
                  setSelectedView("manual");
                  setGlobalToast({text: lang === "sv" ? "Skapade förslag nedan" : "Created suggestion below"});
                }
              }
              if (data.debug) {
                setPrompt(data.debug.prompt);
              }
            } else {
              console.log(data)
            }
          } catch (error) {
            console.log(error);
          }
        }
        return;
      }

      const responseData = decoder.decode(value);

      // If we have received start of finalResponse, add to it
      if (finalResponse.length > 0) {
        finalResponse += responseData;
      } else {
        const aiResponse = responseData.indexOf('{"aiResponse"');
        if (aiResponse > -1) {
          finalResponse = responseData.substring(aiResponse);
        }
      }

      if (finalResponse.length === 0) {
        if (responseData.startsWith("_-_")) {
          updateLastMessage(responseData.substring(3));
        } else {
          streamResponse += responseData;
          updateLastMessage(streamResponse);
        }
      }
    }
  }

  const send = (text: string, aiStart: string|null, uploadFile: UploadFile[]=[]) => {
    const _message = {
      role: "user",
      text: text,
      aiFiles: uploadFile
    };

    let _barrier = barrier;

    if (props.editPart.type === modelType.BEHAVIOR) {
      if (text.startsWith("My barrier ")) {
        _barrier = text.substring(10, text.length - 1);
        setBarrier(_barrier);
      } else if (text.startsWith("Mitt hinder ")) {
        _barrier = text.substring(13, text.length - 1);
        setBarrier(_barrier);
      }
    }

    setEmmaThinking(true);
    setStreamStatus(StreamEnum.THINKING);
    scrollDown();

    const data = {
      message: text,
      messages: messages,
      coachStyle: coachStyle,
      type: props.editPart.type,
      id: props.editPart.id,
      title: title,
      ifWhen: ifWhen,
      IfThen: ifThen,
      barrier: barrier,
      description: description,
      deadline: deadline,
      questions: questions,
      goal: props.viewGoal,
      aiFile: uploadFile,
    };

    const _messages = messages;
    _messages.push(_message);
    _messages.push({role: "assistant", text: "Thinking..."});
    setMessages(_messages);

    api.post("fast_api/client/vision_statement_part/" + props.visionStatement.id, data, [], false, true)
      .then(response => {
        if (response.response.ok && response.response.body) {
          handleStream(response.response.body.getReader()).then(() => {
            setStreamStatus(StreamEnum.DONE);
            setEmmaThinking(false);
          });
        } else {
          alert(response.response.statusText);
        }
      })
      .catch(error => alert(error));
  };

  const noOp = () => {};

  const renderPartHeader = () => {
    if (props.editPart.type === modelType.SUBGOAL) {
      if (props.editPart.description.length === 0) {
        return lang === "sv" ? "Skapa ditt delmål" : "Create your sub-goal";
      } else {
        return lang === "sv" ? "Ändra ditt delmål" : "Edit your sub-goal";
      }
    } else if (props.editPart.type === modelType.ACTIVITY) {
      if (props.editPart.description.length === 0) {
        return lang === "sv" ? "Skapa en aktivitet" : "Create an activity";
      } else {
        return lang === "sv" ? "Ändra din aktivitet" : "Edit your activity";
      }
    } else if (props.editPart.type === "action") {
      if (props.editPart.description.length === 0) {
        return lang === "sv" ? "Skapa en uppgift" : "Create a task";
      } else {
        return lang === "sv" ? "Ändra din uppgift" : "Edit your task";
      }
    } else if (props.editPart.type === modelType.BEHAVIOR) {
      if (!props.editPart.deadline || props.editPart.deadline.length === 0) {
        return lang === "sv" ? "Skapa beteende" : "Create behaviour";
      } else {
        return lang === "sv" ? "Ändra beteende" : "Edit behaviour";
      }
    } else if (props.editPart.type === modelType.LONGTERMGOAL) {
      if (props.editPart.description.length === 0) {
        return lang === "sv" ? "Skapa din målbild" : "Create your Goal";
      } else {
        return lang === "sv" ? "Ändra din målbild" : "Edit your Goal";
      }
    }
  };

  const renderAI = () => {
    const placeholder = lang === "sv" ? "Skriv här..." : "Write here...";
    return (
      <div className="px-4 py-2">
        {renderChat()}

        <div className="relative justify-end">
          <div className="z-gradientback-model"></div>
          <ChatThreadInput
            sendToOtherPage={false}
            handleSendMessage={send}
            onHeightChange={noOp}
            setCoachStyle={setCoachStyle}
            coachStyle={coachStyle}
            startVoice={noOp}
            emmaThinking={emmaThinking}
            placeholder={placeholder}
            hideVoice={true}
          />
        </div>
      </div>
    );
  };

  const renderManual = () => {
    const options = currentOptions;
    const LABEL_BY_TYPE = {
      [modelType.ACTIVITY]:     t("save-activity"),
      [modelType.SUBGOAL]:      t("save-subgoal"),
      [modelType.BEHAVIOR]:     t("save-behavior"),
      [modelType.LONGTERMGOAL]: t("save-longtermgoal"),
    };

    const saveLabel = LABEL_BY_TYPE[props.editPart.type as modelType] ?? t("save");
    const height = "calc(100dvh - " + (narrow ? 218 : 170) + "px)";

    return (
      <div className="p-4 flex flex-col" style={{height: height}}>
        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">
          {
            (props.editPart.type === "action" && props.viewGoal) &&
              <>
                <div className="flex items-center justify-between cursor-pointer"
                     onClick={() => setShowDetails(!showDetails)}>
                  <div className="text-gray-100 font-medium">
                    {lang === "sv" ? "Se delmål" : "See sub goal"}
                  </div>
                  <IconDown aria-hidden="true"
                    className={`h-4 w-4 cursor-pointer text-white! ${showDetails ? "rotate-180" : ""}`}/>
                </div>

                {
                  showDetails &&
                    <>
                      <div className="mt-2 font-medium text-gray-100">{t("title")}:</div>
                      <div className="text-gray-300">{props.viewGoal.title}</div>

                      <div className="mt-2 font-medium text-gray-100">{t("description")}:</div>
                      <div className="text-gray-300">{props.viewGoal.description}</div>

                      <div className="mt-2 font-medium text-gray-100">{t("due-date")}:</div>
                      <div className="text-gray-300">{props.viewGoal.deadline}</div>
                    </>
                }

                <hr className="border-indigo-700 my-2"/>
              </>
          }

          <div className="bg-indigo-900">
            {
              props.editPart.type === modelType.BEHAVIOR ? (
                <>
                  <div className="mt-4 font-medium text-gray-100">{lang === "sv" ? "Om/När" : "If/When"}</div>

                  <div className="mt-2">
                    <input type="text" className="z-input" value={ifWhen}
                           placeholder={lang === "sv" ? "När jag får en svår fråga" : "When I'm asked a difficult question"}
                           onChange={(e) => {
                             setIfWhen(e.target.value);
                           }}/>
                  </div>

                  <div className="mt-4 font-medium text-gray-100">{lang === "sv" ? "Då" : "Then"}</div>

                  <div className="mt-2">
                    <input type="text" className="z-input" value={ifThen}
                           placeholder={lang === "sv" ? "Ska jag pausa i 3 sekunder innan jag svarar" : "I will pause for 3 seconds before answering"}
                           onChange={(e) => {
                             setIfThen(e.target.value);
                           }}/>
                  </div>

                  <div className="mt-4 font-medium text-gray-100">{lang === "sv" ? "Kort titel" : "Short title"}</div>
                  <div className="mt-2">
                    <input type="text" className="z-input" value={title} placeholder={t("title")}
                           onChange={(e) => {
                             setTitle(e.target.value);
                           }}/>
                  </div>

                  <div className="mt-4 font-medium text-gray-100">{lang === "sv" ? "Hur ofta vill du träna på beteendet?" : "How often do you plan to practise this behaviour?"}</div>
                 <div className="mt-2 flex flex-col xl:flex-row gap-2">
  {HOW_OFTEN_OPTIONS.map((opt) => (
    <div
      key={opt.value}
      onClick={() => setHowOften(opt.value)}
      className={[
        'flex-1 rounded-full border border-indigo-700 whitespace-nowrap px-3 py-1 cursor-pointer text-center text-neutral-100!',
        howOften === opt.value ? 'bg-indigo-700' : 'hover:bg-indigo-700',
      ].join(' ')}
    >
      {lang === "sv" ? opt.sv : opt.en}
    </div>
  ))}
</div>
                </>
              ) : (
                <>
                  <div className="mt-4 font-medium text-gray-100">{t("title")}</div>

                  <div className="mt-2">
                    <input type="text" className="z-input" value={title} placeholder={t("title")}
                           onChange={(e) => {
                             setTitle(e.target.value);
                           }}/>
                  </div>

                  <div className="mt-4 font-medium text-gray-100">{t("description")}</div>

                  <div className="mt-2">
                    <textarea className="z-input h-54" value={description} placeholder={t("description")}
                         onChange={(e) => {
                           setDescription(e.target.value);
                         }}/>
                  </div>
                </>
              )
            }

            <div className="mt-4 font-medium text-gray-100 gap-2">
              {modelType.BEHAVIOR === props.editPart.type ? t("practice-behavior-for") : t("set-due-date")}
            </div>
            {
              options &&
                <div className="mt-2">
                  <DueDateBadge
                    options={options}
                    selected={selectedRangeOption}
                    onChange={handleChange}
                    getLanguageString={(opt) => lang === 'sv'
                      ? `${opt.value} ${opt.unit === 'week' ? 'veckor' : 'månader'}`
                      : `${opt.value} ${opt.unit}${opt.value > 1 ? 's' : ''}`
                    }
                  />
                </div>
            }
            <div className="mt-2">
              <DatePicker
                wrapperClassName="w-full"
                className="z-input w-full cursor-pointer"
                selected={deadline}
                placeholderText={
                  lang === "sv"
                    ? "Välj datum"
                    : "Select a date"
                }
                onChange={updateDeadline}
                dateFormat="yyyy-MM-dd"
                minDate={new Date()}
                withPortal
                fixedHeight
              />
            </div>
          </div>
        </div>

        {/* Save button pinned to bottom */}
        <div className="pt-4">
          <CustomButton
            disabled={!isValid()}
            buttonType="blueBase"
            style={{width: "100%"}}
            onClick={() => save()}>
            {saveLabel}
          </CustomButton>
        </div>
      </div>
    );
  };

  const renderBody = () => {
    return (
      <>
        <div className="z-show-sub-1100 px-4 pt-4">
          <ButtonMenu items={[
            {
              title: t("ai-assistant"),
              key: "ai",
              icon: <IconAi className="h-4 w-4 mr-2 text-white" aria-hidden="true" />
            },
            {
              title: t("manual"),
              key: "manual",
              icon: <PencilIcon variant="white" className="h-4 mr-2" alt="edit" />
            },
          ]} selected={selectedView} selectKey={setSelectedView}/>
        </div>
        <div className="z-responsive-2cols flex"> 
          {(!narrow || selectedView === "ai") &&
            <div className={`z-responsive-col1-nowidth md:w-2/3 ${narrow ? "z-responsive-col1" : ""}`}>
              {renderAI()}
            </div>
          }

          {(!narrow || selectedView === "manual") &&
            <div className={`bg-indigo-900 z-responsive-col1-nowidth w-1/3 ${narrow ? "z-responsive-col1" : ""}` + (narrow && selectedView === "ai" ? " hide" : "")}>
              {renderManual()}
            </div>
          }
        </div>
      </>
    );
  };

  const closeWithoutSaving = () => {
    if (!props.visionStatement.description || props.visionStatement.description.length < 1 ||
      !props.visionStatement.deadline || props.visionStatement.deadline.length < 1) {
      if (!props.visionStatement.surveyId) {
        const data = {
          type: "vision_statement",
        };
        api.post("client/vision_statement_delete_part/" + props.visionStatement.id, data).then(response => {
          if (response.json.status === "ok") {
            navigate("/dev-plan");
          }
        });
      } else {
        navigate('/dev-plan');
      }
    } else {
      props.close();
      setCloseNoSave(false);
    }
  };

  const save = () => {
    if (deadline) {
      props.save(title, description, deadline.toLocaleDateString("sv-SE"), barrier, ifWhen, ifThen, howOften);
    }
  };

  const renterInfoModel = () => {
    if (props.editPart.type === modelType.LONGTERMGOAL) {
      return renterLongtermInfoModel();
    } else {
      return renterSubInfoModel();
    }
  };

  const handleChange = (_rangeOption: RangeOption | null, fromDate: Date | null) => {
    setDeadline(fromDate);
  };

  const renterSubInfoModel = () => {
    return (     
      <Modal isOpen={true} onClose={() => setFeedbackInfoModel(false)}>
        <ModalHeader onClose={() => setFeedbackInfoModel(false)}>{t("long-term Goal")}</ModalHeader>
        <ModalBody>
          <div className="mt-2 font-medium text-gray-100">{t("title")}:</div>
          <div className="text-gray-300">{props.visionStatement.title}</div>

          <div className="mt-2 font-medium text-gray-100">{t("description")}:</div>
          <div className="text-gray-300">{props.visionStatement.description}</div>

          <div className="mt-2 font-medium text-gray-100">{t("due-date")}:</div>
          <div className="text-gray-300">{props.visionStatement.deadline}</div>
        </ModalBody>
      </Modal>
    );
  };

  const renterLongtermInfoModel = () => {
    return (     
      <Modal isOpen={true} onClose={() => setFeedbackInfoModel(false)}>
        <ModalHeader onClose={() => setFeedbackInfoModel(false)}>{t("feedback-model-info")}</ModalHeader>
        <ModalBody>
          <div className="mt-4">
            {
              questions.map((question, i) => (
                <div className="bg-indigo-800 rounded-md p-4 mt-4" key={"question_" + i}>
                  <div className="flex gap-x-2">
                    {
                      question.scoreOther > 0 &&
                        <SurveyBadge lang={lang} type="validated" value={question.scoreSelf}
                                     value2={question.scoreOther}/>
                    }
                    {
                      question.focusAreaName.length > 0 &&
                        <div className="z-badge transparent">{question.focusAreaName}</div>
                    }
                  </div>

                  <div className="flex flex-wrap gap-2 mt-2">
                    <div className="z-half">{question.name}</div>

                    <div className="z-half">
                      <TwoBars value1={question.scoreSelf} value2={question.scoreOther} size="small"/>
                      <div className="mt-2 flex">
                        <div className="text-sm">
                          Self-assessment: <span
                          className="z-title2">{question.scoreSelf.toFixed(1)}</span>/5
                        </div>
                        <div className="ml-4 text-sm">
                          Feedback: <span
                          className="z-title2">{question.scoreOther === 0 ? "-" : question.scoreOther.toFixed(1)}</span>/5
                        </div>
                        <div className="ml-4 text-sm">
                          Gap: <span
                          className="z-title2">{question.scoreOther === 0 ? "-" : Math.abs(question.scoreOther - question.scoreSelf).toFixed(1)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            }
          </div>
        </ModalBody>
      </Modal>);
  };

  return (
    <>
      <Modal isOpen={true} onClose={() => setCloseNoSave(true)} size="large">
        <ModalHeader onClose={() => setCloseNoSave(true)}>
          <div className="flex items-center justify-between w-full">
            <div>{renderPartHeader()}</div>
            {
              showfeedbackInfoModel && (
                <div className="pr-4">
                  <CustomButton
                    buttonType="neutralBase"
                    size="sm"
                    style={{ width: "unset" }}
                    onClick={() => setFeedbackInfoModel(true)}
                    leftIcon={<InformationCircleIcon alt="info" />}
                  >
                    {
                      props.editPart.type === modelType.LONGTERMGOAL 
                        ? (lang === "sv" ? "Se feedback" : "See feedback")
                        : (lang === "sv" ? "Se målbild" : "See Goal")
                    }
                  </CustomButton>
                </div>
              )
            }
          </div>
        </ModalHeader>

        <ModalBody noPadding={true}>
          {renderBody()}
        </ModalBody>
      </Modal>
      {
        closeNoSave &&
          <Modal isOpen={closeNoSave} onClose={() => setCloseNoSave(false)}>
            <ModalBody>
              <div className="z-title1">
                {lang === "sv" ? "Vill du stänga utan att spara?" : "Exit without saving?"}
              </div>
            </ModalBody>
            <ModalFooter>
              <CustomButton buttonType="outlineWhiteBase" onClick={() => {
                  setCloseNoSave(false);
                }}>{lang === "sv" ? "Stanna kvar" : "Stay"}</CustomButton>

                <div className="flex gap-x-2 w-full">
                <CustomButton buttonType="dangerBase" onClick={() => closeWithoutSaving()}>
                  {lang === "sv" ? "Stäng utan att spara" : "Exit without saving"}
                </CustomButton>
                
              </div>
            </ModalFooter>
          </Modal>
      }
      {
        feedbackInfoModel && (
          renterInfoModel()
        )
      }
    </>
  );
});

export default LongTermGoalPart;